import React, { useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'

import { cancelSubscription, errText } from './api'

/**
 * Cancelling a subscription. Shared by the list and the detail page.
 *
 * The dialog spells out the consequences, because none of them can be undone from here: shipping
 * on the merchant's own couriers stops at once, auto-renew goes off, and no refund is issued. The
 * reason is required by the backend and lands on the audit trail.
 */
export default function CancelSubscriptionDialog({ sub, onClose, onDone }) {
  const { enqueueSnackbar } = useSnackbar()
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!reason.trim()) { enqueueSnackbar('A reason is required.', { variant: 'error' }); return }
    setSaving(true)
    try {
      await cancelSubscription(sub.id, reason.trim())
      enqueueSnackbar(`${sub.subscriptionNo} cancelled`, { variant: 'success' })
      onDone()
    } catch (e) {
      enqueueSnackbar(errText(e), { variant: 'error' })
    } finally { setSaving(false) }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={saving ? undefined : onClose}>
      <DialogTitle>Cancel {sub.subscriptionNo}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Merchant <strong>{sub.merchantName || sub.merchantOid || sub.merchantId}</strong> ·
          plan <strong>{sub.planCode}</strong> · currently <strong>{sub.status}</strong>
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>This cannot be undone.</strong> Shipping on the merchant&rsquo;s own couriers stops
          immediately and auto-renew is switched off. <strong>No refund is issued</strong> — if one is
          due, raise it with finance separately. To restart, the merchant buys a new plan or you
          activate one for them.
        </Alert>
        <TextField label="Reason (required — goes on the audit trail)" value={reason}
                   onChange={(e) => setReason(e.target.value)} fullWidth size="small"
                   multiline rows={2} autoFocus />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Keep subscription</Button>
        <Button variant="contained" color="error" onClick={submit} disabled={saving || !reason.trim()}>
          {saving ? 'Cancelling…' : 'Cancel subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
